import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sparkles, Check, Loader2, ChevronLeft } from "lucide-react";
import { toast } from "sonner";

export default function Premium() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/pricing").then(({ data }) => setPricing(data)).catch(() => setPricing({ available: false }));
  }, []);

  const upgrade = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "dailylock_premium_monthly",
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't start checkout");
    } finally {
      setLoading(false);
    }
  };

  const isPremium = user?.is_premium;

  return (
    <div className="flex-1 flex flex-col p-6 grain">
      <header className="pt-8">
        <button onClick={() => nav(-1)} className="text-xs tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-1" data-testid="premium-back">
          <ChevronLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> Back
        </button>
      </header>

      <section className="flex-1 flex flex-col justify-center animate-fade-up py-6">
        <div className="flex items-center gap-2 text-primary">
          <Sparkles className="h-4 w-4" strokeWidth={1.5} />
          <p className="text-[11px] tracking-[0.35em] uppercase">Premium</p>
        </div>
        <h1 className="mt-4 font-serif text-5xl leading-[1] tracking-tight">
          Two people.<br />
          <span className="italic text-primary">One helping hand.</span>
        </h1>
        <p className="mt-6 text-muted-foreground max-w-md">
          Same slow, honest rhythm. Just a little more of it.
        </p>

        <ul className="mt-10 space-y-4">
          {[
            "A second exchange partner unlocked every day",
            "Tap any partner message to translate it instantly",
            "Priority in the matching queue when the pool is thin",
            "One clean price. Cancel any time.",
          ].map((line, i) => (
            <li key={i} className="flex gap-3 items-start">
              <span className="mt-1 h-5 w-5 rounded-full bg-primary/15 flex items-center justify-center text-primary shrink-0">
                <Check className="h-3 w-3" strokeWidth={2} />
              </span>
              <p className="text-[15px] leading-relaxed">{line}</p>
            </li>
          ))}
        </ul>

        <div className="mt-12 rounded-3xl border border-border bg-card p-6" data-testid="pricing-card">
          {pricing?.available ? (
            <>
              <p className="text-[11px] tracking-[0.3em] uppercase text-muted-foreground">Monthly</p>
              <p className="mt-1 font-serif text-4xl">
                ${(pricing.amount / 100).toFixed(2)}
                <span className="text-base text-muted-foreground ml-1">/ month</span>
              </p>
              <p className="mt-2 text-sm text-muted-foreground">Billed monthly. Cancel any time.</p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Loading pricing…</p>
          )}
        </div>
      </section>

      <footer className="pb-4 pt-4 space-y-3">
        {isPremium ? (
          <div className="rounded-full h-14 bg-secondary text-secondary-foreground flex items-center justify-center text-base" data-testid="already-premium">
            You're already Premium — thank you.
          </div>
        ) : (
          <Button
            onClick={upgrade}
            disabled={loading || !pricing?.available}
            className="w-full h-14 rounded-full text-base bg-primary hover:bg-primary/90 transition-transform hover:-translate-y-0.5"
            data-testid="upgrade-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> : "Upgrade to Premium"}
          </Button>
        )}
        <p className="text-center text-xs text-muted-foreground">Secure checkout via Stripe.</p>
      </footer>
    </div>
  );
}
